import React, { useState, useEffect, useCallback, useRef } from "react";

type CellState = 0 | 1;
type GridType = CellState[][];

type Props = {};

export default function GameOfLifeGamePage({}: Props) {
  const [grid, setGrid] = useState<GridType>([]);
  const [rows, setRows] = useState(50); // Default to 50x50 grid
  const [cols, setCols] = useState(50);
  const [running, setRunning] = useState(false);
  const [generation, setGeneration] = useState(0);
  const [speed, setSpeed] = useState(50); //  speed control (percentage)
  const [cellSize, setCellSize] = useState(12); // Initial cell size
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawMode, setDrawMode] = useState<0 | 1>(1);
  const [theme, setTheme] = useState<"light" | "dark">("dark"); // Default theme
  const [showRules, setShowRules] = useState(false);

  const runningRef = useRef(running);
  runningRef.current = running;

  const speedRef = useRef(speed);
  speedRef.current = speed;

  const canvasRef = useRef<HTMLDivElement>(null);

  // Helper function to create an empty grid
  const createEmptyGrid = useCallback(
    (): GridType =>
      Array(rows)
        .fill(null)
        .map(() => Array(cols).fill(0) as CellState[]),
    [rows, cols]
  );

  // Randomize the grid
  const randomizeGrid = useCallback((): GridType => {
    return Array(rows)
      .fill(null)
      .map(() =>
        Array(cols)
          .fill(null)
          .map(() => (Math.random() < 0.3 ? 1 : 0) as CellState)
      );
  }, [rows, cols]);

  // Initialize grid
  useEffect(() => {
    resetGrid();

    const handleResize = () => {
      if (canvasRef.current) {
        const maxWidth = Math.min(window.innerWidth - 40, 1200); // Max width with padding
        const maxHeight = window.innerHeight - 250; // Adjust based on your layout

        const newCellSize = Math.min(
          Math.floor(maxWidth / cols),
          Math.floor(maxHeight / rows),
          20 // Maximum cell size
        );

        setCellSize(Math.max(newCellSize, 6)); // Minimum cell size
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Initial size calculation

    return () => window.removeEventListener("resize", handleResize);
  }, [cols, rows]); //  cols and rows

  // Reset the grid
  const resetGrid = useCallback(() => {
    const emptyGrid = createEmptyGrid();
    setGrid(emptyGrid);
    setGeneration(0);
    setRunning(false);
    runningRef.current = false;
  }, [createEmptyGrid]);

  // Helper function to count live neighbors with wrap-around
  const countNeighbors = useCallback(
    (grid: GridType, x: number, y: number): number => {
      let sum = 0;
      for (let i = -1; i <= 1; i++) {
        for (let j = -1; j <= 1; j++) {
          if (i === 0 && j === 0) continue;
          const row = (x + i + rows) % rows;
          const col = (y + j + cols) % cols;
          sum += grid[row][col];
        }
      }
      return sum;
    },
    [rows, cols]
  );

  // Helper function to compute the next generation
  const nextGeneration = useCallback((): GridType => {
    return produce(grid, (gridCopy) => {
      for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++) {
          const neighbors = countNeighbors(grid, i, j);
          if (grid[i][j] === 1) {
            gridCopy[i][j] = neighbors === 2 || neighbors === 3 ? 1 : 0; //Simplify
          } else {
            gridCopy[i][j] = neighbors === 3 ? 1 : 0; //Simplify
          }
        }
      }
    });
  }, [grid, rows, cols, countNeighbors]);

  // Main simulation effect
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (running) {
      interval = setInterval(() => {
        setGrid((prevGrid) => nextGeneration());
        setGeneration((gen) => gen + 1);
      }, 1000 - speedRef.current * 9); //  speed control
    }
    return () => clearInterval(interval);
  }, [running, nextGeneration]);

  // Toggle simulation running state
  const toggleRunning = () => {
    setRunning(!running);
    runningRef.current = !running;
  };

  // Step through one generation
  const handleStep = () => {
    if (!running) {
      setGrid(nextGeneration());
      setGeneration((gen) => gen + 1);
    }
  };

  // Toggle a cell's state
  const toggleCell = (i: number, j: number) => {
    if (running) return;
    const newGrid = produce(grid, (gridCopy) => {
      gridCopy[i][j] = drawMode;
    });
    setGrid(newGrid);
  };

  // Mouse event handlers for drawing
  const handleMouseDown = (i: number, j: number) => {
    setIsDrawing(true);
    toggleCell(i, j);
  };

  const handleMouseOver = (i: number, j: number) => {
    if (isDrawing) {
      toggleCell(i, j);
    }
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  // Handle theme toggle
  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  const handleGridSizeChange = (size: "small" | "medium" | "large") => {
    if (running) return; // Prevent grid size change while running

    switch (size) {
      case "small":
        setRows(30);
        setCols(40);
        break;
      case "medium":
        setRows(40);
        setCols(60);
        break;
      case "large":
        setRows(50);
        setCols(80);
        break;
    }
  };

  useEffect(() => {
    resetGrid();
  }, [rows, cols, resetGrid]);

  // Color schemes
  const colors = {
    light: {
      background: "#f8f9fa",
      text: "#343a40",
      cell: {
        alive: "#0066cc",
        dead: "#ffffff",
        border: "#e9ecef",
      },
      button: {
        primary: "#0066cc",
        secondary: "#6c757d",
        text: "#ffffff",
      },
      canvas: "#ffffff",
    },
    dark: {
      background: "#22272e",
      text: "#adbac7",
      cell: {
        alive: "#58a6ff",
        dead: "#2d333b",
        border: "#444c56",
      },
      button: {
        primary: "#2ea043",
        secondary: "#444c56",
        text: "#ffffff",
      },
      canvas: "#2d333b",
    },
  };

  const currentTheme = colors[theme];

  // CSS Styles (using the first page's styles)
  const styles = {
    container: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      padding: "20px",
      backgroundColor: currentTheme.background,
      color: currentTheme.text,
      transition: "all 0.3s ease",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    header: {
      textAlign: "center" as const,
      marginBottom: "20px",
      width: "100%",
    },
    title: {
      fontSize: "2.5rem",
      marginBottom: "10px",
      background: "linear-gradient(90deg, #58a6ff, #2ea043)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      textShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
    subtitle: {
      fontSize: "1rem",
      marginBottom: "20px",
      opacity: 0.8,
    },
    gridContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column" as const,
      marginBottom: "20px",
    },
    canvas: {
      display: "grid",
      backgroundColor: currentTheme.canvas,
      borderRadius: "8px",
      overflow: "hidden",
      boxShadow:
        theme === "dark"
          ? "0 4px 20px rgba(0, 0, 0, 0.5)"
          : "0 4px 20px rgba(0, 0, 0, 0.1)",
      transition: "all 0.3s ease",
      border: `2px solid ${currentTheme.cell.border}`,
    },
    cell: (alive: boolean) => ({
      width: `${cellSize}px`,
      height: `${cellSize}px`,
      backgroundColor: alive ? currentTheme.cell.alive : currentTheme.cell.dead,
      border: `1px solid ${currentTheme.cell.border}`,
      transition: "background-color 0.2s ease",
      cursor: running ? "not-allowed" : "pointer",
    }),
    controls: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      width: "100%",
      maxWidth: "800px",
      marginTop: "20px",
      gap: "15px",
    },
    controlRow: {
      display: "flex",
      flexWrap: "wrap" as const,
      justifyContent: "center",
      gap: "10px",
      margin: "10px 0",
      width: "100%",
    },
    button: (primary: boolean = false) => ({
      padding: "8px 16px",
      borderRadius: "6px",
      backgroundColor: primary
        ? currentTheme.button.primary
        : currentTheme.button.secondary,
      color: currentTheme.button.text,
      border: "none",
      cursor: "pointer",
      fontWeight: "bold",
      fontSize: "14px",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "100px",
      boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    }),
    slider: {
      width: "100%",
      maxWidth: "300px",
      accentColor: currentTheme.button.primary, // Use theme color for slider
      margin: "0 10px",
    },
    speedControl: {
      display: "flex",
      alignItems: "center",
      width: "100%",
      maxWidth: "350px", // Limit width for responsiveness
      justifyContent: "space-between",
    },
    info: {
      padding: "10px 20px",
      backgroundColor:
        theme === "dark" ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.05)",
      borderRadius: "8px",
      marginTop: "20px",
      fontSize: "14px",
      display: "flex",
      flexDirection: "column" as const,
      gap: "5px",
      width: "100%",
      maxWidth: "800px",
    },
    infoRow: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
    },
    label: {
      fontWeight: "bold",
      marginRight: "10px",
    },
    select: {
      padding: "8px 16px",
      borderRadius: "6px",
      backgroundColor: currentTheme.button.secondary,
      color: currentTheme.button.text,
      border: "none",
      cursor: "pointer",
      fontWeight: "bold",
      fontSize: "14px",
      minWidth: "150px",
    },
    rulesButton: {
      background: "none",
      border: "none",
      cursor: "pointer",
      color: currentTheme.button.primary,
      fontSize: "14px",
      fontWeight: "bold",
      padding: "5px 10px",
      borderRadius: "5px",
      transition: "background-color 0.2s",
      marginTop: "10px",
      textDecoration: "underline",
    },
    modal: {
      display: "flex",
      position: "fixed" as const,
      top: "0",
      left: "0",
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0,0,0,0.7)",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    },
    modalContent: {
      backgroundColor: currentTheme.background,
      padding: "30px",
      borderRadius: "10px",
      maxWidth: "600px",
      width: "90%",
      maxHeight: "80vh",
      overflow: "auto",
      position: "relative" as const,
      boxShadow: "0 5px 30px rgba(0,0,0,0.3)",
    },
    modalClose: {
      position: "absolute" as const,
      top: "15px",
      right: "15px",
      fontSize: "24px",
      cursor: "pointer",
      background: "none",
      border: "none",
      color: currentTheme.text,
    },
    modalTitle: {
      fontSize: "24px",
      marginBottom: "20px",
      borderBottom: `1px solid ${currentTheme.cell.border}`,
      paddingBottom: "10px",
    },
    footer: {
      marginTop: "30px",
      fontSize: "12px",
      opacity: 0.7,
      textAlign: "center" as const,
    },
  };

  // Produce function for immutable updates
  function produce<T>(currentState: T, recipe: (draft: T) => void): T {
    const nextState = JSON.parse(JSON.stringify(currentState)) as T;
    recipe(nextState);
    return nextState;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Conway's Game of Life</h1>
        <p style={styles.subtitle}>
          A cellular automaton where cells live or die based on simple rules
        </p>
      </div>

      <div style={styles.gridContainer}>
        <div
          ref={canvasRef}
          style={{
            ...styles.canvas,
            gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
          }}
          onMouseLeave={() => setIsDrawing(false)}
          onMouseUp={handleMouseUp}
        >
          {grid.map((rows, i) =>
            rows.map((cell, j) => (
              <div
                key={`${i}-${j}`}
                style={styles.cell(cell === 1)}
                onMouseDown={() => handleMouseDown(i, j)}
                onMouseOver={() => handleMouseOver(i, j)}
              />
            ))
          )}
        </div>
      </div>

      <div style={styles.controls}>
        {/* Control Row 1: Start/Pause, Clear, Step, Theme */}
        <div style={styles.controlRow}>
          <button style={styles.button(running)} onClick={toggleRunning}>
            {running ? "Pause" : "Start"}
          </button>
          <button style={styles.button()} onClick={resetGrid}>
            Clear
          </button>
          <button
            style={styles.button()}
            onClick={handleStep}
            disabled={running}
          >
            Step
          </button>

          <button
            style={styles.button()}
            onClick={() => {
              resetGrid();
              setTimeout(() => setGrid(randomizeGrid()), 0);
            }}
          >
            Randomize
          </button>
          <button style={styles.button()} onClick={toggleTheme}>
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
        </div>

        {/* Control Row 2: Speed Control */}
        <div style={styles.controlRow}>
          <div style={styles.speedControl}>
            <span>Speed: </span>
            <input
              type="range"
              min="1"
              max="100"
              value={speed}
              onChange={(e) => setSpeed(parseInt(e.target.value))}
              style={styles.slider}
            />
            <span>{speed}%</span>
          </div>
        </div>

        {/* Control Row 3: Grid Size, Draw Mode */}
        <div style={styles.controlRow}>
          <select
            onChange={(e) =>
              handleGridSizeChange(
                e.target.value as "small" | "medium" | "large"
              )
            }
            value={rows === 30 ? "small" : rows === 50 ? "large" : "medium"}
            style={styles.select}
            disabled={running}
          >
            <option value="small">Small Grid</option>
            <option value="medium">Medium Grid</option>
            <option value="large">Large Grid</option>
          </select>

          <select
            value={drawMode}
            onChange={(e) => setDrawMode(parseInt(e.target.value) as 0 | 1)}
            style={styles.select}
            disabled={running}
          >
            <option value="1">Draw Live Cells</option>
            <option value="0">Draw Dead Cells</option>
          </select>
        </div>

        {/* Info Section */}
        <div style={styles.info}>
          <div style={styles.infoRow}>
            <span style={styles.label}>Generation:</span>
            <span>{generation}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.label}>Grid Size:</span>
            <span>
              {rows} × {cols}
            </span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.label}>Live Cells:</span>
            <span>
              {grid.reduce(
                (acc, row) =>
                  acc + row.reduce((sum: any, cell) => sum + cell, 0),
                0
              )}
            </span>
          </div>
          <button style={styles.rulesButton} onClick={() => setShowRules(true)}>
            View Game Rules
          </button>
        </div>
      </div>
      {showRules && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <button
              style={styles.modalClose}
              onClick={() => setShowRules(false)}
            >
              ×
            </button>
            <h2 style={styles.modalTitle}>Conway's Game of Life Rules</h2>
            <div>
              <p>
                The Game of Life is a cellular automaton devised by
                mathematician John Conway in 1970.
              </p>
              <p>
                It takes place on a grid where each cell is either alive or
                dead, following these simple rules:
              </p>
              <br />
              <ol>
                <li>
                  <strong>Birth:</strong> A dead cell with exactly 3 live
                  neighbors becomes alive.
                </li>
                <li>
                  <strong>Survival:</strong> A live cell with 2 or 3 live
                  neighbors continues to live.
                </li>
                <li>
                  <strong>Death by isolation:</strong> A live cell with fewer
                  than 2 live neighbors dies.
                </li>
                <li>
                  <strong>Death by overcrowding:</strong> A live cell with more
                  than 3 live neighbors dies.
                </li>
              </ol>
              <br />
              <p>
                These simple rules lead to fascinating patterns and behaviors:
              </p>
              <ul>
                <li>
                  <strong>Still lifes:</strong> Patterns that don't change (like
                  blocks)
                </li>
                <li>
                  <strong>Oscillators:</strong> Patterns that repeat
                  periodically
                </li>
                <li>
                  <strong>Spaceships:</strong> Patterns that move across the
                  grid (like the glider)
                </li>
                <li>
                  <strong>Guns:</strong> Patterns that continuously emit
                  spaceships
                </li>
              </ul>
              <br />
              <p>
                Click and drag on the grid to draw your own patterns, or select
                a predefined pattern to see these principles in action!
              </p>
            </div>
          </div>
        </div>
      )}

      <div style={styles.footer}>
        Conway's Game of Life © 2025 - Created for your P2P Game Platform
      </div>
    </div>
  );
}
